import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Calendar, Phone, Loader2, Check, X, MessageSquare, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { createQuoteRequest, createUnclaimedQuoteRequest, trackVenueView, trackVenueInquiry, trackPhoneClick, trackWhatsAppClick, trackWhatsAppChannelClick, trackUnclaimedWhatsAppClick } from '../services/api';
import { getImageUrl } from '../utils/imageOptimization';
import { trackWhatsAppGA4, trackPhoneCallGA4, trackQuoteRequestGA4, trackVenueViewGA4 } from '../utils/analytics';
import axios from 'axios';
import { addRecentlyViewed } from '../utils/recentlyViewed';
import FavoriteButton from '../components/FavoriteButton';
import AuthModal from '../components/AuthModal';
import QuoteWizard from '../components/QuoteWizard';
import WhatsAppPreviewModal from '../components/WhatsAppPreviewModal';
import { createVenueVisit, sendVisitVerificationCode, verifyAndCreateVisit } from '../services/customerApi';

// Lazy loaded components for performance
const SmartPackageBuilder = lazy(() => import('../components/SmartPackageBuilder'));

// Venue detail sub-components
import VenueGallery from '../components/venue-detail/VenueGallery';
import VenueInfoCard from '../components/venue-detail/VenueInfoCard';
import VenueContactGrid from '../components/venue-detail/VenueContactGrid';
import VenueQuickContact from '../components/venue-detail/VenueQuickContact';
import VenueMapSection from '../components/venue-detail/VenueMapSection';
import VenueReviews from '../components/venue-detail/VenueReviews';
import VenueFAQ from '../components/venue-detail/VenueFAQ';
import VenuePackages from '../components/venue-detail/VenuePackages';
import { VenueVideoCard, VenueVerticalVideo, VenueClaimBanner, VenueAbout, VenueFeatures, VenueRelatedLocations, VenueQASection, VenueUnclaimedNote } from '../components/venue-detail/VenueExtras';
import { isMobilePhone, formatWhatsAppNumber, isPremiumPlan, isVenueClaimed, turkishToEnglish } from '../components/venue-detail/venueHelpers';
import NisanHesaplayiciCompactBanner from '../components/NisanHesaplayiciCompactBanner';
import NisanHediyelikTeaserBanner from '../components/NisanHediyelikTeaserBanner';
import UnclaimedAutoModal from '../components/venue-detail/UnclaimedAutoModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const VenueDetailPage = () => {
  const { slug, venueSlug, city, district } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const actualSlug = venueSlug || slug;
  
  // Core state
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [inquiryTracked, setInquiryTracked] = useState(false);
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [isBot, setIsBot] = useState(false);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  
  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', eventDate: '', eventType: 'Söz', guestCount: '', message: ''
  });
  const [claimFormData, setClaimFormData] = useState({ claimantName: '', claimantEmail: '', claimantPhone: '' });
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  
  // Platform settings
  const [platformSettings, setPlatformSettings] = useState({ wizardFormEnabled: true });
  const [similarVenues, setSimilarVenues] = useState([]);
  
  // Sticky bar & scroll
  const [showStickyBar, setShowStickyBar] = useState(false);
  const aboutSectionRef = useRef(null);
  const viewTrackedRef = useRef(false);
  
  // Auth & WhatsApp modals
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppActionType, setWhatsAppActionType] = useState(null);
  const [showUnclaimedWhatsAppModal, setShowUnclaimedWhatsAppModal] = useState(false);
  const [unclaimedWhatsAppMessage, setUnclaimedWhatsAppMessage] = useState('');

  // Venue Visit state
  const [venueVisitEnabled, setVenueVisitEnabled] = useState(false);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [visitFormData, setVisitFormData] = useState({ visitDate: '', visitTime: '', name: '', phone: '', consentGiven: false });
  const [creatingVisit, setCreatingVisit] = useState(false);
  const [visitStep, setVisitStep] = useState('form');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [codeResendTimer, setCodeResendTimer] = useState(0);

  // Smart Package state
  const [smartPricingEnabled, setSmartPricingEnabled] = useState(false);
  const [smartPackageData, setSmartPackageData] = useState(null);

  // Q&A state (consolidated endpoint'ten gelir)
  const [venueQuestions, setVenueQuestions] = useState([]);

  // ── Memoized Values ──
  const getVenueFullUrl = useCallback(() => {
    if (!venue) return '';
    const baseUrl = process.env.REACT_APP_FRONTEND_URL || 'https://davetevibul.com';
    if (venue.cityName && venue.districtName) {
      return `${baseUrl}/${turkishToEnglish(venue.cityName)}/${turkishToEnglish(venue.districtName)}/${venue.slug}`;
    }
    return `${baseUrl}/${venue.slug}`;
  }, [venue]);

  const getShareableUrl = useCallback(() => {
    if (!venue) return '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://davetevibul.com';
    if (venue.cityName && venue.districtName) {
      return `${backendUrl}/api/venue-preview/${turkishToEnglish(venue.cityName)}/${turkishToEnglish(venue.districtName)}/${venue.slug}`;
    }
    return getVenueFullUrl();
  }, [venue, getVenueFullUrl]);

  // ── Callbacks ──
  const handleSmartPackageReady = useCallback((packageData) => {
    setSelectedPackage({
      name: `Akıllı Paket (${packageData.tierRange} kişi)`,
      price: packageData.total,
      description: packageData.summary,
      features: packageData.extras,
      isSmartPackage: true,
      smartData: packageData
    });
    setSmartPackageData(packageData);
    const quoteSection = document.querySelector('[data-testid="quote-wizard"]') || document.querySelector('.sticky');
    if (quoteSection) {
      quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // no-op
    }
    toast({ title: 'Paketiniz hazır!', description: 'Teklif formunu doldurup gönderebilirsiniz.', duration: 3000 });
  }, []);

  const handleShare = useCallback((platform) => {
    const url = getVenueFullUrl();
    const title = `${venue?.name} - ${venue?.cityName} ${venue?.districtName} Davet Evi`;
    if (platform === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(title + '\n\n' + url)}`, '_blank');
    } else if (platform === 'copy') {
      navigator.clipboard.writeText(url).then(() => {
        toast({ title: 'Link Kopyalandı!', description: 'Bağlantı panonuza kopyalandı.', duration: 3000 });
      });
    }
  }, [venue, getVenueFullUrl]);

  const handleBackNavigation = useCallback(() => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else if (venue?.cityId && venue?.districtId) {
      navigate(`/davet-evleri?cityId=${venue.cityId}&districtIds=${venue.districtId}`);
    } else {
      navigate('/davet-evleri');
    }
  }, [location, navigate, venue]);

  const getBackButtonText = useCallback(() => {
    if (location.state?.from || window.history.length > 1) return 'Geri Dön';
    if (venue?.districtName) return `${venue.districtName}'deki Davet Evleri`;
    return 'Tüm Davet Evleri';
  }, [location, venue]);

  const getVenuesPageUrl = useCallback(() => {
    if (venue?.citySlug && venue?.districtSlug) {
      return `/davet-evleri?city=${encodeURIComponent(venue.cityName)}&district=${encodeURIComponent(venue.districtName)}`;
    }
    return '/davet-evleri';
  }, [venue]);

  const handlePhoneCall = useCallback(async () => {
    if (venue?.phone) {
      try {
        await trackPhoneClick(venue.id);
        trackPhoneCallGA4(venue.name, venue.id, 'phone_button');
      } catch (e) { /* ignore */ }
      window.location.href = `tel:${venue.phone}`;
    }
  }, [venue]);

  const handleWhatsAppClick = useCallback(async (phoneNumber, message = '') => {
    if (venue?.id) {
      try {
        await trackWhatsAppClick(venue.id);
        trackWhatsAppGA4(venue.name, venue.id, 'whatsapp_button');
      } catch (e) { /* ignore */ }
    }
    window.location.href = `https://wa.me/${formatWhatsAppNumber(phoneNumber)}?text=${encodeURIComponent(message)}`;
  }, [venue]);

  const handleUnclaimedWhatsAppClick = useCallback(() => {
    if (!venue) return;
    const message = `Merhaba, mekanınızı davetevibul.com üzerinden inceledim.\nYaklaşan etkinliğim için bilgi almak istiyorum.\nMüsait olduğunuzda dönüş yapabilir misiniz?\nTeşekkürler.`;
    setUnclaimedWhatsAppMessage(message);
    setShowUnclaimedWhatsAppModal(true);
  }, [venue]);

  const handleUnclaimedWhatsAppSend = useCallback(async (message) => {
    if (!venue) return;
    try {
      await trackUnclaimedWhatsAppClick({ venueId: venue.id, venueName: venue.name, venuePhone: venue.phone, message, source: 'venue_detail_page' });
      trackWhatsAppGA4(venue.name, venue.id, 'unclaimed_whatsapp');
    } catch (e) { /* ignore */ }
    setShowUnclaimedWhatsAppModal(false);
  }, [venue]);

  const scrollToQuoteForm = useCallback(() => {
    const el = document.getElementById('quote-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const onWhatsAppAction = useCallback((type) => {
    setWhatsAppActionType(type);
    setShowWhatsAppModal(true);
  }, []);

  // ── Effects ──
  useEffect(() => { window.scrollTo(0, 0); }, [actualSlug]);

  // Hash-based scroll (from listing cards)
  useEffect(() => {
    if (venue && window.location.hash === '#smart-package-builder') {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="smart-package-builder"]');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 1000);
    }
  }, [venue]);

  useEffect(() => {
    const handleScroll = () => {
      if (aboutSectionRef.current) {
        const top = aboutSectionRef.current.getBoundingClientRect().top;
        setShowStickyBar(top < window.innerHeight * 0.5);
      } else {
        const pct = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        setShowStickyBar(pct > 30);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    setIsBot(/bot|crawl|spider|slurp|googlebot|bingbot|yandex|baidu|duckduck|facebook|twitter|whatsapp/i.test(ua));
  }, []);

  useEffect(() => {
    viewTrackedRef.current = false;
    loadVenue();
  }, [actualSlug]);

  // Expose current venue slug on window so LiveChatWidget can send it to backend
  // as chat context. Cleared on unmount / when leaving the venue page.
  useEffect(() => {
    if (venue?.slug) {
      window.__DAVET_VENUE_SLUG__ = venue.slug;
    }
    return () => {
      if (window.__DAVET_VENUE_SLUG__) {
        delete window.__DAVET_VENUE_SLUG__;
      }
    };
  }, [venue?.slug]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!isLightboxOpen || !venue?.images) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
      else if (e.key === 'ArrowLeft' && selectedImage > 0) setSelectedImage(selectedImage - 1);
      else if (e.key === 'ArrowRight' && selectedImage < venue.images.length - 1) setSelectedImage(selectedImage + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, selectedImage, venue]);

  // Code resend timer
  useEffect(() => {
    if (codeResendTimer > 0) {
      const timer = setTimeout(() => setCodeResendTimer(codeResendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [codeResendTimer]);

  // Schema.org JSON-LD
  useEffect(() => {
    if (!venue) return;
    const existing = document.getElementById('venue-schema-jsonld');
    if (existing) existing.remove();

    // KVKK: Onaysız mekanlarda tam adres ve telefon structured data'ya dahil edilmez
    const claimedForSchema = isVenueClaimed(venue);
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": venue.name,
      "description": venue.description || venue.googleEditorialSummary || `${venue.name} - ${venue.cityName} ${venue.districtName} davet evi`,
      "image": venue.images?.map(img => getImageUrl(img)) || [],
      "address": { "@type": "PostalAddress", ...(claimedForSchema ? { "streetAddress": venue.address || "" } : {}), "addressLocality": venue.districtName, "addressRegion": venue.cityName, "addressCountry": "TR" },
      "url": getVenueFullUrl(),
      ...(claimedForSchema && venue.phone ? { "telephone": venue.phone } : {}),
      "priceRange": "₺₺₺"
    };
    // KVKK: Onaysız mekanlarda GPS koordinatları da structured data'ya dahil edilmez (konum ifşası)
    if (claimedForSchema) {
      if (venue.latitude && venue.longitude) {
        schema.geo = { "@type": "GeoCoordinates", "latitude": venue.latitude, "longitude": venue.longitude };
      } else if (venue.location?.coordinates) {
        schema.geo = { "@type": "GeoCoordinates", "latitude": venue.location.coordinates[1], "longitude": venue.location.coordinates[0] };
      }
    }
    if (!venue.hideGoogleReviews && (venue.googleReviewCount > 0 || venue.reviewCount > 0)) {
      schema.aggregateRating = { "@type": "AggregateRating", "ratingValue": String(venue.googleRating || venue.rating || 5), "reviewCount": String(venue.googleReviewCount || venue.reviewCount || 0), "bestRating": "5", "worstRating": "1" };
    }
    if (!venue.hideGoogleReviews && venue.googleReviews?.length > 0) {
      const itemReviewed = { "@type": "LocalBusiness", "name": venue.name, "address": { "@type": "PostalAddress", "addressLocality": venue.districtName, "addressRegion": venue.cityName, "addressCountry": "TR" } };
      schema.review = venue.googleReviews.slice(0, 5).filter(r => r?.text && (r.authorName || r.author)).map(review => ({
        "@type": "Review", "itemReviewed": itemReviewed,
        "author": { "@type": "Person", "name": review.authorName || review.author },
        "reviewRating": { "@type": "Rating", "ratingValue": review.rating || 5, "bestRating": 5, "worstRating": 1 },
        "reviewBody": review.text
      }));
    }
    const script = document.createElement('script');
    script.id = 'venue-schema-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { const s = document.getElementById('venue-schema-jsonld'); if (s) s.remove(); };
  }, [venue, getVenueFullUrl]);

  // Similar venues artık venue-full endpoint'inden geliyor, ayrı istek YOK

  // ── Load Venue (CONSOLIDATED: tek istek ile tüm veri) ──
  const loadVenue = async () => {
    try {
      setLoading(true);
      setError(null);
      if (actualSlug?.endsWith('-davet-evleri')) return;

      // TEK İSTEK: venue + smartPricing + availability + similarVenues + settings + mapsKey
      const fullRes = await axios.get(`${BACKEND_URL}/api/venue-full/${actualSlug}`);
      const { venue: venueData, smartPricing, availability, similarVenues: similarData, settings, googleMapsKey: mapsKey, questions } = fullRes.data;

      setVenue(venueData);

      // Google Maps key
      if (mapsKey) {
        setGoogleMapsApiKey(mapsKey);
        sessionStorage.setItem('googleMapsApiKey', mapsKey);
      } else {
        const cachedMapsKey = sessionStorage.getItem('googleMapsApiKey');
        if (cachedMapsKey) setGoogleMapsApiKey(cachedMapsKey);
      }

      // Smart pricing
      if (smartPricing?.enabled) setSmartPricingEnabled(true);

      // Availability
      if (availability) {
        setVenueVisitEnabled(availability.enabled || false);
        setAvailabilitySlots(availability.availabilitySlots || []);
      }

      // Similar venues
      if (similarData?.length > 0) {
        setSimilarVenues(similarData.filter(v => v.id !== venueData.id).slice(0, 5));
      }

      // Platform settings
      if (settings) {
        const s = { wizardFormEnabled: settings.wizardFormEnabled !== false };
        setPlatformSettings(s);
        sessionStorage.setItem('platformSettings', JSON.stringify(s));
        sessionStorage.setItem('platformSettingsTime', Date.now().toString());
      }

      // Q&A
      if (questions) setVenueQuestions(questions);

      // Background refresh (phone, reviews) - lazy, arka planda (DB'ye ekstra yük yok)
      const isClaimed = venueData.isClaimed === true || (venueData.ownerId && venueData.ownerId !== '') || ['pro', 'premium'].includes(venueData.currentPlan?.toLowerCase());
      const shouldRefresh = (venueId, type) => {
        const ck = `google_refresh_${type}_${venueId}`;
        const last = localStorage.getItem(ck);
        if (last) { const hrs = (Date.now() - parseInt(last)) / 3600000; return hrs >= (type === 'reviews' ? 168 : 24); }
        return true;
      };
      const markRefreshed = (venueId, type) => localStorage.setItem(`google_refresh_${type}_${venueId}`, Date.now().toString());

      if (!isClaimed && venueData.googlePlaceId && shouldRefresh(venueData.id, 'phone')) {
        axios.get(`${BACKEND_URL}/api/venues/${venueData.id}/refresh-phone`).then(r => {
          markRefreshed(venueData.id, 'phone');
          if (r.data?.updated && r.data?.phone) setVenue(prev => ({ ...prev, phone: r.data.phone }));
        }).catch(() => {});
      }
      if (venueData.googlePlaceId && shouldRefresh(venueData.id, 'reviews')) {
        axios.get(`${BACKEND_URL}/api/venues/${venueData.id}/refresh-reviews`).then(r => {
          markRefreshed(venueData.id, 'reviews');
          if (r.data?.updated) setVenue(prev => ({ ...prev, rating: r.data.rating, googleRating: r.data.rating, reviewCount: r.data.reviewCount, googleReviews: r.data.reviews, googleOpeningHours: r.data.openingHours, instagramUrl: r.data.instagramUrl, googleDescription: r.data.description, googleAccessibility: r.data.accessibility, googleParking: r.data.parking, googlePayment: r.data.payment }));
        }).catch(() => {});
      }

      // LCP Preload
      if (venueData.images?.[0]) {
        const link = document.createElement('link');
        link.rel = 'preload'; link.as = 'image';
        link.href = venueData.images[0].includes('cloudinary') ? venueData.images[0].replace('/upload/', '/upload/f_auto,q_auto:good,w_800/') : venueData.images[0];
        link.fetchPriority = 'high';
        document.head.appendChild(link);
      }
      addRecentlyViewed(venueData);
      if (venueData.packages?.length > 0) setSelectedPackage(venueData.packages[0]);

      if (!viewTrackedRef.current) {
        viewTrackedRef.current = true;
        trackVenueView(venueData.id).catch(() => {});
        trackVenueViewGA4(venueData.name, venueData.id, venueData.cityName, venueData.districtName);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading venue:', error);
      setError('Mekan bulunamadı');
      setLoading(false);
      setTimeout(() => navigate('/davet-evleri'), 2000);
    }
  };

  // ── Wizard Submit ──
  const handleWizardSubmit = async (wizardData) => {
    setIsSubmitting(true);
    const eventTypeLabels = { 'isteme-nisan': 'İsteme & Nişan', 'kina': 'Kına', 'dogum-gunu': 'Doğum Günü', 'bebek-mevlidi': 'Bebek Mevlidi', 'etkinlik': 'Etkinlik' };
    const cateringLabels = { 'kendim': 'İkramlıkları kendim getireceğim', 'ikramli': 'İkramlı İstiyorum' };
    const photographyLabels = { 'yok': 'İstemiyorum', 'fotograf': 'Fotoğraf', 'hikaye': 'Hikaye', 'fotograf-hikaye': 'Fotoğraf + Hikaye', 'fotograf-video': 'Fotoğraf + Video', 'video': 'Video' };
    const serviceLabels = { 'garson': 'Garson Olsun', 'kendimiz': 'Servisi kendimiz yaparız' };

    try {
      const formatMonth = (monthValue) => {
        const [month, year] = monthValue.split('-');
        const monthNames = ['Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran', 'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik'];
        return `${monthNames[parseInt(month) - 1]} ${year}`;
      };

      const buildWhatsAppMessage = () => `Merhaba,\ndavetevibulcom uzerinden ${venue.name} icin teklif almak istiyorum.\n\nTalep Detaylari\n—\nOrganizasyon: ${eventTypeLabels[wizardData.eventType] || wizardData.eventType}\nKisi Sayisi: ${wizardData.guestCount}\nIkram: ${cateringLabels[wizardData.catering] || wizardData.catering}\nFotograf / Video: ${photographyLabels[wizardData.photography] || wizardData.photography}\nServis: ${serviceLabels[wizardData.service] || wizardData.service}\nPlanlanan Ay: ${formatMonth(wizardData.eventMonth)}\n\nIletisim Bilgileri\nIsim: ${wizardData.fullName}\nTelefon: ${wizardData.phone}`;

      const notes = `Talep Detaylari\n—\nOrganizasyon: ${eventTypeLabels[wizardData.eventType] || wizardData.eventType}\nKisi Sayisi: ${wizardData.guestCount}\nIkram: ${cateringLabels[wizardData.catering] || wizardData.catering}\nFotograf / Video: ${photographyLabels[wizardData.photography] || wizardData.photography}\nServis: ${serviceLabels[wizardData.service] || wizardData.service}\nPlanlanan Ay: ${formatMonth(wizardData.eventMonth)}`;

      const isClaimed = venue.isClaimed === true;
      const hasOwner = venue.ownerId != null && venue.ownerId !== '';
      const hasMobilePhone = isMobilePhone(venue.phone);
      const shouldUseWhatsApp = !isClaimed && !hasOwner && hasMobilePhone;
      const isUnclaimed = !isClaimed && !hasOwner;

      const quoteData = {
        venueId: venue.id, venueName: venue.name, fullName: wizardData.fullName, phone: wizardData.phone, email: '',
        eventType: eventTypeLabels[wizardData.eventType] || wizardData.eventType, eventDate: wizardData.eventMonth,
        guestCount: parseInt(wizardData.guestCount.split('-')[0]) || 50, notes
      };

      try {
        if (isUnclaimed) await createUnclaimedQuoteRequest(quoteData);
        else await createQuoteRequest(quoteData);
      } catch (apiError) { console.error('API kayıt hatası:', apiError); }

      trackQuoteRequestGA4(venue.name, venue.id, wizardData.eventType, null);

      if (shouldUseWhatsApp) {
        const phoneNumber = venue.whatsappNumber || venue.phone;
        const message = buildWhatsAppMessage();
        try {
          if (isUnclaimed) await trackUnclaimedWhatsAppClick({ venueId: venue.id, message });
          else await trackWhatsAppClick(venue.id, message);
          trackWhatsAppGA4(venue.name, venue.id, 'wizard_form');
        } catch (e) { /* ignore */ }
        setTimeout(() => { window.open(`https://wa.me/${formatWhatsAppNumber(phoneNumber)}?text=${encodeURIComponent(message)}`, '_blank'); }, 300);
        toast({ title: 'WhatsApp Açılıyor', description: 'Talepleriniz WhatsApp üzerinden mekan sahibine iletilecektir.' });
      } else {
        toast({ title: 'Talebiniz Alındı!', description: 'Mekan sahibi talebinizden haberdar edildi.' });
      }
    } catch (error) {
      console.error('Error creating quote request:', error);
      toast({ title: 'Hata!', description: error.response?.data?.detail || 'Teklif talebi oluşturulamadı.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Claim Submit ──
  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!claimFormData.claimantName || !claimFormData.claimantEmail || !claimFormData.claimantPhone) {
      toast({ title: 'Hata!', description: 'Lütfen tüm alanları doldurun.', variant: 'destructive' });
      return;
    }
    setClaimSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/venues/claim-request`, { venueId: venue.id, ...claimFormData });
      toast({ title: 'Başarılı!', description: 'Sahiplenme talebiniz alındı.' });
      setShowClaimDialog(false);
      setClaimFormData({ claimantName: '', claimantEmail: '', claimantPhone: '' });
    } catch (error) {
      toast({ title: 'Hata!', description: error.response?.data?.detail || 'Sahiplenme talebi gönderilemedi.', variant: 'destructive' });
    } finally {
      setClaimSubmitting(false);
    }
  };

  // ── Visit Handlers ──
  const handleSendVerificationCode = async () => {
    if (!visitFormData.visitDate || !visitFormData.visitTime) { toast({ title: 'Uyarı', description: 'Lütfen tarih ve saat seçiniz.', variant: 'destructive' }); return; }
    if (!visitFormData.name || visitFormData.name.trim().length < 2) { toast({ title: 'Uyarı', description: 'Lütfen adınızı ve soyadınızı girin.', variant: 'destructive' }); return; }
    const phone = visitFormData.phone.replace(/\s/g, '');
    if (!phone.startsWith('5') || phone.length !== 10) { toast({ title: 'Uyarı', description: 'Geçerli bir telefon numarası girin (5XX XXX XX XX)', variant: 'destructive' }); return; }
    if (!visitFormData.consentGiven) { toast({ title: 'Uyarı', description: 'Bilgilerinizin paylaşılmasını kabul etmelisiniz.', variant: 'destructive' }); return; }
    try {
      setSendingCode(true);
      const result = await sendVisitVerificationCode({ venueId: venue.id, name: visitFormData.name.trim(), phone, visitDate: visitFormData.visitDate, visitTime: visitFormData.visitTime });
      setMaskedPhone(result.maskedPhone || `0${phone.slice(0, 3)}***${phone.slice(-2)}`);
      setVisitStep('verify');
      setCodeResendTimer(60);
      toast({ title: 'Kod Gönderildi', description: `Doğrulama kodu ${result.maskedPhone || 'telefonunuza'} gönderildi.` });
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Doğrulama kodu gönderilemedi.', variant: 'destructive' });
    } finally { setSendingCode(false); }
  };

  const handleVerifyAndCreateVisit = async () => {
    if (!verificationCode || verificationCode.length !== 6) { toast({ title: 'Uyarı', description: 'Lütfen 6 haneli doğrulama kodunu girin.', variant: 'destructive' }); return; }
    try {
      setVerifyingCode(true);
      await verifyAndCreateVisit({ venueId: venue.id, phone: visitFormData.phone.replace(/\s/g, ''), code: verificationCode, name: visitFormData.name.trim(), visitDate: visitFormData.visitDate, visitTime: visitFormData.visitTime, consentGiven: visitFormData.consentGiven });
      toast({ title: 'Randevu Talebi Gönderildi!', description: 'Mekan sahibi talebinizi inceleyecek ve size telefonla dönüş yapacak.' });
      setShowVisitDialog(false); setVisitStep('form'); setVerificationCode('');
      setVisitFormData({ visitDate: '', visitTime: '', name: '', phone: '', consentGiven: false });
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Doğrulama başarısız.', variant: 'destructive' });
    } finally { setVerifyingCode(false); }
  };

  const getAvailableTimesForDate = useCallback((dateStr) => {
    if (!dateStr || !availabilitySlots.length) return [];
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
    const slots = availabilitySlots.filter(slot => slot.dayOfWeek === dayOfWeek);
    const times = [];
    slots.forEach(slot => {
      const [startHour, startMin] = slot.startTime.split(':').map(Number);
      const [endHour, endMin] = slot.endTime.split(':').map(Number);
      let current = startHour * 60 + startMin;
      const end = endHour * 60 + endMin;
      while (current < end) { times.push(`${String(Math.floor(current / 60)).padStart(2, '0')}:${String(current % 60).padStart(2, '0')}`); current += 30; }
    });
    return times;
  }, [availabilitySlots]);

  // ── Render ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Mekan yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Mekan Bulunamadı</h1>
          <p className="text-gray-600 mb-6">{error || 'Aradığınız mekan bulunamadı.'}</p>
          <Button onClick={handleBackNavigation} className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800">Geri Dön</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{isVenueClaimed(venue) ? `${venue.name} - ${venue.cityName} ${venue.districtName} Davet Evi | ${venue.packages?.length > 0 ? `${venue.packages.length} Paket Seçeneği | ` : ''}${venue.capacity?.min && venue.capacity?.max ? `${venue.capacity.min}-${venue.capacity.max} Kişilik | ` : ''}Nişan, Söz, Kına Organizasyonu` : `${venue.name} - ${venue.cityName} ${venue.districtName} Davet Evi | Nişan, Söz, Kına Mekanı`}</title>
        <meta name="description" content={`${venue.name} - ${venue.cityName} ${venue.districtName}'te${venue.capacity?.min && venue.capacity?.max ? ` ${venue.capacity.min}-${venue.capacity.max} kişilik kapasiteli` : ''} davet evi. ${venue.packages?.length || 0} farklı paket seçeneği.`} />
        <meta name="keywords" content={`${venue.name}, ${venue.cityName} davet evi, ${venue.districtName} davet salonu, nişan salonu, söz yeri, kına gecesi mekanı`} />
        <link rel="canonical" href={getVenueFullUrl()} />
        <meta property="og:type" content="business.business" />
        <meta property="og:title" content={`${venue.name} - ${venue.districtName} Davet Evi`} />
        <meta property="og:description" content={`${venue.name} - ${venue.cityName} ${venue.districtName}'te davet evi. ${venue.packages?.length || 0} farklı paket.`} />
        <meta property="og:url" content={getVenueFullUrl()} />
        {venue.images?.[0] && <meta property="og:image" content={getImageUrl(venue.images[0])} />}
        <meta property="og:locale" content="tr_TR" />
        <meta property="og:site_name" content="DavetEviBul.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        {venue.images?.[0] && <link rel="preload" as="image" href={venue.images[0].includes('cloudinary') ? venue.images[0].replace('/upload/', '/upload/f_auto,q_auto:good,w_800/') : venue.images[0]} fetchPriority="high" />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": "https://davetevibul.com/" },
            { "@type": "ListItem", "position": 2, "name": `${venue.cityName} Davet Evleri`, "item": `https://davetevibul.com/${turkishToEnglish(venue.cityName || '')}-davet-evleri` },
            { "@type": "ListItem", "position": 3, "name": `${venue.districtName} Davet Evleri`, "item": `https://davetevibul.com/${turkishToEnglish(venue.cityName || '')}/${turkishToEnglish(venue.districtName || '')}` },
            { "@type": "ListItem", "position": 4, "name": venue.name, "item": getVenueFullUrl() }
          ]
        })}</script>
      </Helmet>

      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <Button variant="ghost" onClick={handleBackNavigation} className="mb-2 text-sm sm:text-base hover:bg-gray-100">
            <ArrowLeft className="w-4 h-4 mr-2" />{getBackButtonText()}
          </Button>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-3 sm:px-4 py-2">
          <nav className="flex items-center text-sm text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-pink-600 transition-colors bg-transparent border-0 cursor-pointer">Ana Sayfa</button>
            <span className="mx-2">/</span>
            <button onClick={() => navigate(venue.cityName ? `/davet-evleri?city=${encodeURIComponent(venue.cityName)}` : '/davet-evleri')} className="hover:text-pink-600 transition-colors bg-transparent border-0 cursor-pointer">{venue.cityName}</button>
            <span className="mx-2">/</span>
            <button onClick={() => navigate(getVenuesPageUrl())} className="hover:text-pink-600 transition-colors bg-transparent border-0 cursor-pointer">{venue.districtName}</button>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium truncate">{venue.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 flex flex-col">
            <VenueGallery venue={venue} selectedImage={selectedImage} setSelectedImage={setSelectedImage} isLightboxOpen={isLightboxOpen} setIsLightboxOpen={setIsLightboxOpen} />
            
            {/* Favorite Button */}
            <div className="flex items-center justify-between bg-white rounded-2xl shadow-md p-4 border border-gray-100">
              <FavoriteButton venue={venue} onAuthRequired={() => setShowAuthModal(true)} />
              {isVenueClaimed(venue) && venue.phone && (
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-pink-600" />
                  <span className="text-sm font-medium text-gray-700">Google</span>
                </span>
              )}
            </div>

            <VenueVideoCard venue={venue} />
            <VenueInfoCard venue={venue} onShare={handleShare} />
            
            {/* Contact Grid */}
            <VenueContactGrid venue={venue} onPhoneCall={handlePhoneCall} onUnclaimedWhatsAppClick={handleUnclaimedWhatsAppClick} />

            {/* Nişan Maliyet Hesaplayıcı Banner - Telefon kartının altında */}
            <NisanHesaplayiciCompactBanner cityName={venue?.cityName} />

            {/* Nişan Hediyelik — Yakında (form yok, sadece tıklama = Telegram bildirimi) */}
            <NisanHediyelikTeaserBanner venue={venue} />

            {/* Smart Package Builder */}
            {smartPricingEnabled && venue?.id && (
              <Suspense fallback={<div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-pink-600 mx-auto" /></div>}>
                <SmartPackageBuilder venueId={venue.id} venueName={venue.name} onPackageReady={handleSmartPackageReady} />
              </Suspense>
            )}

            {/* Mobile Wizard Form - Sahipsiz mekanlarda */}
            {!smartPricingEnabled && !isVenueClaimed(venue) && (
              <div className="lg:hidden mb-6 relative">
                <div className="opacity-40">
                  <QuoteWizard venue={venue} onSubmit={handleWizardSubmit} isSubmitting={isSubmitting} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-2xl">
                  <div className="bg-amber-100 border border-amber-300 rounded-xl px-4 py-3 shadow-lg">
                    <p className="text-amber-800 text-sm font-medium text-center">Bu özellik, yalnızca doğrulanmış<br/>mekânlarda aktiftir.</p>
                  </div>
                </div>
              </div>
            )}

            <VenueQuickContact venue={venue} smartPricingEnabled={smartPricingEnabled} onWhatsAppAction={onWhatsAppAction} />

            {/* Mobile Quote Form */}
            {!smartPricingEnabled && isVenueClaimed(venue) && venue?.quoteFormEnabled !== false && (
              <div className="lg:hidden mt-6" id="quote-form">
                <QuoteWizard venue={venue} onSubmit={handleWizardSubmit} isSubmitting={isSubmitting} />
              </div>
            )}

            <VenueClaimBanner venue={venue} isBot={isBot} />
            <VenueMapSection venue={venue} googleMapsApiKey={googleMapsApiKey} />
            <VenueAbout venue={venue} aboutSectionRef={aboutSectionRef} />
            <VenueFAQ venue={venue} />

            <VenuePackages venue={venue} selectedPackage={selectedPackage} setSelectedPackage={setSelectedPackage} />
            <VenueQASection venue={venue} questions={venueQuestions} />
            <VenueRelatedLocations venue={venue} />
            <VenueVerticalVideo venue={venue} />
            <VenueFeatures venue={venue} />
            <VenueUnclaimedNote venue={venue} />
            <VenueReviews venue={venue} />
          </div>

          {/* Right Column - Desktop Sidebar */}
          {!smartPricingEnabled && (
            <div className="hidden lg:block lg:col-span-1">
              <div className="sticky top-6 relative">
                <div className={`${!isVenueClaimed(venue) ? 'opacity-40' : ''}`}>
                  <QuoteWizard venue={venue} onSubmit={handleWizardSubmit} isSubmitting={isSubmitting} />
                </div>
                {!isVenueClaimed(venue) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-2xl">
                    <div className="bg-amber-100 border border-amber-300 rounded-xl px-4 py-3 shadow-lg">
                      <p className="text-amber-800 text-sm font-medium text-center">Bu özellik, yalnızca doğrulanmış<br/>mekânlarda aktiftir.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Claim Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Mekan Sahiplenme Başvurusu</DialogTitle>
            <DialogDescription>Bu mekanın sahibiyseniz, aşağıdaki formu doldurarak sahiplenme talebinde bulunabilirsiniz.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClaimSubmit} className="space-y-4">
            <div><Label htmlFor="claimantName">Ad Soyad *</Label><Input id="claimantName" value={claimFormData.claimantName} onChange={(e) => setClaimFormData({...claimFormData, claimantName: e.target.value})} placeholder="Adınız ve soyadınız" required /></div>
            <div><Label htmlFor="claimantEmail">E-posta *</Label><Input id="claimantEmail" type="email" value={claimFormData.claimantEmail} onChange={(e) => setClaimFormData({...claimFormData, claimantEmail: e.target.value})} placeholder="ornek@email.com" required /></div>
            <div><Label htmlFor="claimantPhone">Telefon *</Label><Input id="claimantPhone" type="tel" value={claimFormData.claimantPhone} onChange={(e) => setClaimFormData({...claimFormData, claimantPhone: e.target.value})} placeholder="05XX XXX XX XX" required /></div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4"><p className="text-sm text-gray-700"><strong>Mekan:</strong> {venue?.name}</p><p className="text-sm text-gray-700 mt-1"><strong>Adres:</strong> {venue?.address}</p></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowClaimDialog(false)} disabled={claimSubmitting}>İptal</Button>
              <Button type="submit" className="bg-gradient-to-r from-pink-600 to-pink-700" disabled={claimSubmitting}>{claimSubmitting ? 'Gönderiliyor...' : 'Başvuru Gönder'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sticky Bottom Bar - Mobile */}
      {!smartPricingEnabled && showStickyBar && isVenueClaimed(venue) && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl border-t border-gray-200 z-50 safe-bottom md:hidden">
          <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3">
            <div className="max-w-6xl mx-auto">
              <div className={`grid gap-0 w-full ${isVenueClaimed(venue) ? 'grid-cols-3' : (venue?.phone ? 'grid-cols-2' : 'grid-cols-1')}`}>
                {venue?.phone && (
                  <Button onClick={handlePhoneCall} variant="outline" className="w-full h-12 sm:h-14 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300 font-semibold text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 rounded-l-xl rounded-r-none">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5" /><span>Ara</span>
                  </Button>
                )}
                {isVenueClaimed(venue) && ((['pro', 'premium'].includes(venue?.currentPlan?.toLowerCase()) && (venue?.whatsappNumber || isMobilePhone(venue?.phone)))) && (
                  <Button onClick={() => { const pn = venue?.whatsappNumber || venue?.phone; handleWhatsAppClick(pn, `Merhaba,\n\ndavetevibulcom uzerinden ${venue?.name} isimli isletmenizi inceledim.\n\nYaklasan etkinligimiz icin bilgi rica ederim.\n\nTesekkurler.`); }} className="w-full h-12 sm:h-14 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 rounded-none border-0">
                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" /><span>WhatsApp</span>
                  </Button>
                )}
                {(venue?.currentPlan === 'Pro' || venue?.currentPlan === 'Premium') && venue?.quoteFormEnabled !== false && (venue?.whatsappNumber || isMobilePhone(venue?.phone)) && (
                  <Button onClick={scrollToQuoteForm} className="w-full h-12 sm:h-14 bg-gradient-to-r from-pink-600 to-pink-700 text-white font-semibold text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 rounded-r-xl rounded-l-none">
                    <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" /><span>Teklif Al</span>
                  </Button>
                )}
                {!isVenueClaimed(venue) && (
                  <Button onClick={() => navigate(`/isortagi-basvuru/${venue?.slug}`)} className={`w-full h-12 sm:h-14 bg-gradient-to-r from-orange-500 to-pink-600 text-white font-semibold text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 ${venue?.phone ? 'rounded-r-xl rounded-l-none' : 'rounded-xl'}`}>
                    <span>Bu Mekan Size Mi Ait?</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onSuccess={() => navigate('/hesabim')} />
      <WhatsAppPreviewModal isOpen={showWhatsAppModal} onClose={() => setShowWhatsAppModal(false)} venueName={venue?.name} phoneNumber={venue?.whatsappNumber || venue?.phone} actionType={whatsAppActionType} onSend={async (message) => { try { await trackWhatsAppClick(venue.id, message, whatsAppActionType); trackWhatsAppGA4(venue.name, venue.id, whatsAppActionType); } catch (e) { /* tracking best-effort */ } }} />
      <WhatsAppPreviewModal isOpen={showUnclaimedWhatsAppModal} onClose={() => setShowUnclaimedWhatsAppModal(false)} venueName={venue?.name} phoneNumber={venue?.phone} actionType="info" customMessage={unclaimedWhatsAppMessage} onSend={handleUnclaimedWhatsAppSend} hideQuickInfo={true} />

      {/* Onaylı olmayan mekan için otomatik açılan iş ortağı davet modal'ı
          (bots'a render edilmez → Google normal içerik görür, cloaking yok) */}
      {!isVenueClaimed(venue) && <UnclaimedAutoModal venue={venue} isBot={isBot} />}

      {/* Visit Dialog */}
      <Dialog open={showVisitDialog} onOpenChange={(open) => { setShowVisitDialog(open); if (!open) { setVisitStep('form'); setVerificationCode(''); } }}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-purple-600" />Ziyaret Randevusu Al</DialogTitle>
            <DialogDescription>{venue?.name} mekanını yerinde görmek için randevu talep edin.</DialogDescription>
          </DialogHeader>
          {visitStep === 'form' ? (
            <>
              <div className="space-y-4 py-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-800 font-medium mb-2">Müsait Günler:</p>
                  <div className="flex flex-wrap gap-2">
                    {availabilitySlots.length > 0 ? [...new Set(availabilitySlots.map(s => s.dayOfWeek))].sort().map(day => (
                      <span key={day} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">{['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'][day]}</span>
                    )) : <span className="text-sm text-purple-600">Müsaitlik bilgisi yükleniyor...</span>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-base font-semibold">Tarih Seçin *</Label>
                  <div className="grid grid-cols-5 gap-2">
                    {(() => { const dates = []; const today = new Date(); for (let i = 0; i < 60; i++) { const date = new Date(today); date.setDate(today.getDate() + i); const dow = date.getDay() === 0 ? 6 : date.getDay() - 1; if (availabilitySlots.some(s => s.dayOfWeek === dow)) { dates.push(date); } if (dates.length >= 5) break; } return dates.map(date => { const dateStr = date.toISOString().split('T')[0]; const isSelected = visitFormData.visitDate === dateStr; return (<button key={dateStr} type="button" onClick={() => setVisitFormData(prev => ({ ...prev, visitDate: dateStr, visitTime: '' }))} className={`p-2 sm:p-3 rounded-xl border-2 transition-all text-center ${isSelected ? 'border-purple-500 bg-purple-500 text-white shadow-lg scale-105' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}><div className="text-[10px] sm:text-xs font-medium opacity-80">{date.toLocaleDateString('tr-TR', { weekday: 'short' })}</div><div className="text-lg sm:text-xl font-bold">{date.getDate()}</div><div className="text-[10px] sm:text-xs opacity-80">{date.toLocaleDateString('tr-TR', { month: 'short' })}</div></button>); }); })()}
                  </div>
                </div>
                {visitFormData.visitDate && (
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Saat Seçin *</Label>
                    {getAvailableTimesForDate(visitFormData.visitDate).length > 0 ? (
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                        {getAvailableTimesForDate(visitFormData.visitDate).map(time => (<button key={time} type="button" onClick={() => setVisitFormData(prev => ({ ...prev, visitTime: time }))} className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${visitFormData.visitTime === time ? 'border-purple-500 bg-purple-500 text-white shadow-md' : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}>{time}</button>))}
                      </div>
                    ) : <div className="text-center py-4 bg-yellow-50 rounded-lg border border-yellow-200"><p className="text-sm text-yellow-800">Bu gün için müsait randevu saati bulunmuyor.</p></div>}
                  </div>
                )}
                {visitFormData.visitDate && visitFormData.visitTime && (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-sm text-green-800 font-medium">Seçiminiz:</p>
                      <p className="text-green-900 font-semibold mt-1">{new Date(visitFormData.visitDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} - {visitFormData.visitTime}</p>
                    </div>
                    <div className="space-y-3 pt-2 border-t">
                      <h4 className="font-semibold text-slate-700">İletişim Bilgileriniz</h4>
                      <div className="space-y-2"><Label htmlFor="visit-name">Ad Soyad *</Label><Input id="visit-name" placeholder="Adınız ve soyadınız" value={visitFormData.name} onChange={(e) => setVisitFormData(prev => ({ ...prev, name: e.target.value }))} /></div>
                      <div className="space-y-2"><Label htmlFor="visit-phone">Telefon *</Label><div className="flex items-center gap-2"><span className="text-slate-500 text-sm">+90</span><Input id="visit-phone" placeholder="5XX XXX XX XX" value={visitFormData.phone} onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setVisitFormData(prev => ({ ...prev, phone: v })); }} className="flex-1" /></div></div>
                      <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border"><input type="checkbox" id="consent-checkbox" checked={visitFormData.consentGiven} onChange={(e) => setVisitFormData(prev => ({ ...prev, consentGiven: e.target.checked }))} className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500" /><label htmlFor="consent-checkbox" className="text-sm text-slate-600">İletişim bilgilerimin mekan sahibiyle paylaşılmasını kabul ediyorum.<span className="text-slate-400 block text-xs mt-1">Randevu onay/red bildirimi için telefonunuza SMS gönderilecektir.</span></label></div>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowVisitDialog(false)} className="w-full sm:w-auto">İptal</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto" onClick={handleSendVerificationCode} disabled={sendingCode || !visitFormData.visitDate || !visitFormData.visitTime || !visitFormData.name || !visitFormData.phone || !visitFormData.consentGiven}>
                  {sendingCode ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kod Gönderiliyor...</> : <><Phone className="w-4 h-4 mr-2" />Doğrulama Kodu Gönder</>}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4"><Phone className="w-8 h-8 text-purple-600" /></div>
                  <h3 className="text-lg font-semibold text-slate-800">Doğrulama Kodu</h3>
                  <p className="text-sm text-slate-500 mt-1">{maskedPhone} numarasına gönderilen 6 haneli kodu girin</p>
                </div>
                <div className="flex justify-center"><Input type="text" inputMode="numeric" maxLength={6} placeholder="• • • • • •" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="text-center text-2xl tracking-[0.5em] font-mono w-48 h-14" /></div>
                <div className="text-center">{codeResendTimer > 0 ? <p className="text-sm text-slate-400">Yeni kod göndermek için {codeResendTimer} saniye bekleyin</p> : <button onClick={handleSendVerificationCode} disabled={sendingCode} className="text-sm text-purple-600 hover:text-purple-700 font-medium">Kodu Tekrar Gönder</button>}</div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-purple-600 font-medium mb-1">Randevu Bilgileri:</p>
                  <p className="text-sm text-purple-800 font-semibold">{new Date(visitFormData.visitDate).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })} - {visitFormData.visitTime}</p>
                  <p className="text-xs text-purple-600 mt-1">{visitFormData.name}</p>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => { setVisitStep('form'); setVerificationCode(''); }} className="w-full sm:w-auto">Geri</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto" onClick={handleVerifyAndCreateVisit} disabled={verifyingCode || verificationCode.length !== 6}>
                  {verifyingCode ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Doğrulanıyor...</> : <><Check className="w-4 h-4 mr-2" />Randevuyu Onayla</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenueDetailPage;
